import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
} from '@mui/icons-material';

import { StoreFormData, ProductData } from './types/store.types';

interface StorePreviewProps {
  formData: StoreFormData;
}

const StorePreview: React.FC<StorePreviewProps> = ({ formData }) => {
  const {
    name,
    description,
    location,
    address,
    city,
    state,
    contactEmail,
    contactPhone,
    website,
    products,
    customTheme,
    showMap,
    showSocialShare,
    coverImage,
    logo,
  } = formData;

  const primaryColor = customTheme?.primaryColor || '#1976d2';
  const secondaryColor = customTheme?.secondaryColor || '#dc004e';
  const fontFamily = customTheme?.fontFamily || 'Roboto';

  return (
    <Box
      sx={{
        fontFamily,
        maxWidth: 1200,
        mx: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 3,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          color: 'white',
          p: 3,
          position: 'relative',
        }}
      >
        {coverImage && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${URL.createObjectURL(coverImage)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.3,
              zIndex: 0,
            }}
          />
        )}
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {logo && (
              <Avatar
                src={URL.createObjectURL(logo)}
                sx={{ width: 60, height: 60, border: '2px solid white' }}
              />
            )}
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {name || 'Nome da Loja'}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                {description || 'Descrição da loja'}
              </Typography>
            </Box>
          </Box>

          {/* Informações de contato */}
          <Grid container spacing={2}>
            {location && (
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon fontSize="small" />
                  <Typography variant="body2">{location}</Typography>
                </Box>
              </Grid>
            )}
            
            {contactPhone && (
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">{contactPhone}</Typography>
                </Box>
              </Grid>
            )}
            
            {contactEmail && (
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2">{contactEmail}</Typography>
                </Box>
              </Grid>
            )}
            
            {website && (
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WebsiteIcon fontSize="small" />
                  <Typography variant="body2">{website}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      {/* Navegação */}
      <Box sx={{ bgcolor: 'grey.100', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="text" size="small">Todos</Button>
          <Button variant="text" size="small">Destaques</Button>
          <Button variant="text" size="small">Promoções</Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showSocialShare && (
            <>
              <IconButton size="small">
                <ShareIcon />
              </IconButton>
              <IconButton size="small">
                <FavoriteIcon />
              </IconButton>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<CartIcon />}
            sx={{
              bgcolor: primaryColor,
              '&:hover': { bgcolor: secondaryColor },
            }}
          >
            Carrinho (0)
          </Button>
        </Box>
      </Box>

      {/* Produtos */}
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Produtos ({products?.length || 0})
        </Typography>
        
        {products && products.length > 0 ? (
          <Grid container spacing={3}>
            {products.slice(0, 6).map((product, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <ProductCard product={product} primaryColor={primaryColor} secondaryColor={secondaryColor} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhum produto adicionado ainda
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adicione produtos na etapa anterior para vê-los aqui
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.100', p: 3, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Sobre a Loja
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description || 'Descrição da loja aparecerá aqui'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Contato
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {address && (
                <Typography variant="body2" color="text.secondary">
                  {address}, {city} - {state}
                </Typography>
              )}
              {contactPhone && (
                <Typography variant="body2" color="text.secondary">
                  {contactPhone}
                </Typography>
              )}
              {contactEmail && (
                <Typography variant="body2" color="text.secondary">
                  {contactEmail}
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Links Rápidos
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Termos de Uso
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Política de Privacidade
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Central de Ajuda
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © 2024 {name || 'Minha Loja'}. Todos os direitos reservados.
          </Typography>
          
          {showSocialShare && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small">
                <ShareIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Componente para card de produto
const ProductCard: React.FC<{ product: ProductData; primaryColor: string; secondaryColor: string }> = ({ 
  product, 
  primaryColor,
  secondaryColor
}) => {
  const { name, description, price, originalPrice, quantity, tags, featured } = product;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', p: 2 }}>
        {/* Placeholder para imagem do produto */}
        <Box
          sx={{
            width: '100%',
            height: 200,
            bgcolor: 'grey.200',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Imagem do Produto
          </Typography>
        </Box>

        {/* Badges */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 1 }}>
          {featured && (
            <Chip label="Destaque" size="small" color="primary" />
          )}
          {originalPrice && originalPrice > price && (
            <Chip label="Promoção" size="small" color="error" />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, p: 0 }}>
          <Typography variant="h6" gutterBottom noWrap>
            {name || 'Nome do Produto'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
            {description || 'Descrição do produto'}
          </Typography>

          {/* Preços */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="success.main" component="span">
              R$ {price?.toFixed(2) || '0,00'}
            </Typography>
            {originalPrice && originalPrice > price && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ textDecoration: 'line-through', ml: 1 }}
              >
                R$ {originalPrice.toFixed(2)}
              </Typography>
            )}
          </Box>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
              {tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          {/* Estoque */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Estoque: {quantity || 0}
          </Typography>
        </CardContent>

        {/* Botão de ação */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              bgcolor: primaryColor,
              '&:hover': { bgcolor: secondaryColor },
            }}
            disabled={!quantity || quantity <= 0}
          >
            {quantity && quantity > 0 ? 'Adicionar ao Carrinho' : 'Indisponível'}
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

export default StorePreview;
